<template>
    <div class="custom-need-form">
        <el-form :model="customForm" :rules="customRules" ref="customForm" label-width="170px" class="form-box">
            <!-- 定制模式：personal-个人定制 / crowdfunding-多人众筹 -->
            <el-form-item label="定制模式" prop="customMode">
                <el-radio-group v-model="customForm.customMode" @change="onModeChange">
                    <el-radio-button label="personal">个人定制</el-radio-button>
                    <!-- <el-radio-button label="crowdfunding">多人众筹</el-radio-button> -->
                </el-radio-group>
            </el-form-item>

            <!-- 几人众筹：仅多人众筹模式显示 -->
            <el-form-item v-if="customForm.customMode === 'crowdfunding'" label="几人众筹" prop="crowdNumber">
                <el-input v-model.number="customForm.crowdNumber" type="number" :min="2" style="max-width: 240px;"
                    placeholder="请输入参与众筹的人数">
                    <template slot="append">人</template>
                </el-input>
                <!-- 提示性文字：众筹需多人平摊预算金额 -->
                <div class="crowd-tip">
                    设置的几人众筹要平摊预算金额<span v-if="perPersonAmount !== null">（预计每人平摊约 {{ perPersonAmount }} 元）</span>
                </div>
            </el-form-item>

            <!-- 众筹截止时间：仅多人众筹模式显示，对应接口 crowdDeadline -->
            <el-form-item v-if="customForm.customMode === 'crowdfunding'" label="众筹截止时间" prop="crowdDeadline">
                <el-date-picker v-model="customForm.crowdDeadline" type="datetime" style="max-width: 320px;"
                    value-format="yyyy-MM-dd HH:mm:ss" format="yyyy-MM-dd HH:mm" placeholder="请选择众筹截止时间"
                    :picker-options="deadlinePickerOptions" />
            </el-form-item>

            <!-- 定制软件标题（对应接口 title，<= 30 字符） -->
            <el-form-item label="定制软件标题" prop="title">
                <el-input v-model="customForm.title" maxlength="30" show-word-limit
                    placeholder="请输入定制软件名称 / 标题（不超过 30 字符）" />
            </el-form-item>

            <!-- 需求详细说明（对应接口 demand，<= 500 字符） -->
            <el-form-item label="需求详细说明" prop="description">
                <el-input v-model="customForm.description" type="textarea" :rows="5" maxlength="500" show-word-limit
                    placeholder="请详细描述所需软件的功能、使用场景、技术诉求等需求说明（不超过 500 字符）" />
            </el-form-item>

            <!-- 预算金额（对应接口 budget，number/bigdecimal，必需，单位：元，>=0） -->
            <el-form-item label="预算金额(元)" prop="budget">
                <el-input v-model.number="customForm.budget" type="number" :min="0" style="max-width: 240px;"
                    placeholder="请输入预算金额（单位：元）">
                    <template slot="append">元</template>
                </el-input>
            </el-form-item>

            <!-- 预算补充说明（对应接口 budgetRemark，string，可选） -->
            <el-form-item label="预算补充说明" prop="budgetRemark">
                <el-input v-model="customForm.budgetRemark" type="textarea" :rows="3" maxlength="500" show-word-limit
                    placeholder="预算补充说明（可选）" />
            </el-form-item>

            <!-- 需求附件（对应接口 demandFile，array[string]，可选） -->
            <el-form-item label="需求附件" prop="demandFile">
                <el-upload class="attach-upload" action="" :auto-upload="false" :show-file-list="true" :limit="1"
                    accept=".doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf,.txt,.md,.csv,.jpg,.jpeg,.png,.gif,.bmp,.webp,.svg,.zip,.rar,.7z,.tar,.gz"
                    :file-list="attachList" :on-change="chooseAttach" :on-remove="onAttachRemove">
                    <el-button size="small" type="primary" :loading="attachUploading">选择文件</el-button>
                    <div slot="tip" class="upload-tip">
                        支持常见文档、图片、压缩包等格式，单个文件，可选<br />
                        文档：.doc .docx .xls .xlsx .ppt .pptx .pdf .txt .md .csv<br />
                        图片：.jpg .jpeg .png .gif .bmp .webp .svg<br />
                        压缩包：.zip .rar .7z .tar .gz
                    </div>
                </el-upload>
            </el-form-item>

            <el-form-item>
                <el-button type="primary" @click="submitCustom" :loading="saveBtnLoading">提交需求</el-button>
                <el-button @click="resetCustomForm">重置</el-button>
            </el-form-item>
        </el-form>

        <!-- 发布成功居中动画 -->
        <transition name="success-pop">
            <div class="success-mask" v-if="showSuccess">
                <div class="success-card">
                    <svg class="success-icon" viewBox="0 0 52 52">
                        <circle class="success-circle" cx="26" cy="26" r="24" fill="none" />
                        <path class="success-check" fill="none" d="M14 27 l8 8 l16 -18" />
                    </svg>
                    <div class="success-text">发布成功</div>
                    <div class="success-sub">等待审核</div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { softwareCustomDemandRelease } from '@/api/software'
import { queryUuIdInfo } from '@/api/common'

export default {
    name: 'CustomNeedForm',
    data() {
        return {
            // 上传业务ID（需求附件上传用，独立获取，避免依赖父页面）
            id: null,
            // 发布定制软件需求表单
            customForm: {
                customMode: 'personal', // personal-个人定制 / crowdfunding-多人众筹
                crowdNumber: '', // 几人众筹（仅多人众筹模式必填，正整数，对应接口 crowdNumber）
                crowdDeadline: '', // 众筹截止时间（仅多人众筹模式必填，对应接口 crowdDeadline，格式 yyyy-MM-dd HH:mm:ss）
                title: '', // 定制软件标题（对应接口 title，<= 30 字符）
                description: '', // 需求详细说明（对应接口 demand，<= 500 字符）
                demandFile: [], // 需求附件列表（对应接口 demandFile，array[string]，可选）
                budget: '', // 预算金额（对应接口 budget，number/bigdecimal，必需，>=0，单位：元）
                budgetRemark: '', // 预算补充说明（对应接口 budgetRemark，string，可选）
            },
            customRules: {
                customMode: { required: true, message: '请选择定制模式', trigger: 'change' },
                crowdNumber: [
                    // 多人众筹模式必填；个人定制时清空即可（onModeChange 已清空）
                    {
                        required: true,
                        validator: (rule, value, callback) => {
                            if (this.customForm.customMode !== 'crowdfunding') return callback()
                            if (value === '' || value === null || value === undefined) {
                                return callback(new Error('请填写参与人数'))
                            }
                            const num = Number(value)
                            if (isNaN(num)) return callback(new Error('参与人数必须为数字'))
                            if (!Number.isInteger(num)) return callback(new Error('参与人数必须为整数'))
                            if (num < 2) return callback(new Error('参与人数至少为 2 人'))
                            callback()
                        },
                        trigger: ['change', 'blur'],
                    },
                ],
                crowdDeadline: [
                    // 多人众筹模式必填；个人定制时清空即可（onModeChange 已清空）
                    {
                        required: true,
                        validator: (rule, value, callback) => {
                            if (this.customForm.customMode !== 'crowdfunding') return callback()
                            if (!value) return callback(new Error('请选择众筹截止时间'))
                            const ts = new Date(value.replace(/-/g, '/')).getTime()
                            if (!isNaN(ts) && ts <= Date.now()) {
                                return callback(new Error('众筹截止时间必须晚于当前时间'))
                            }
                            callback()
                        },
                        trigger: ['change', 'blur'],
                    },
                ],
                title: [
                    { required: true, message: '请输入定制软件标题', trigger: 'change' },
                    { max: 30, message: '标题不超过 30 字符', trigger: 'change' },
                ],
                description: [
                    { required: true, message: '请填写需求详细说明', trigger: 'change' },
                    { max: 500, message: '需求详细说明不超过 500 字符', trigger: 'change' },
                ],
                budget: [
                    { required: true, message: '请输入预算金额', trigger: 'change' },
                    {
                        validator: (rule, value, callback) => {
                            if (value === '' || value === null || value === undefined) return callback()
                            const num = Number(value)
                            if (isNaN(num)) return callback(new Error('预算金额必须为数字'))
                            if (num < 0) return callback(new Error('预算金额不能小于 0'))
                            callback()
                        },
                        trigger: 'change',
                    },
                ],
                // demandFile / budgetRemark 为可选字段，无需必填校验
            },
            attachList: [],
            attachUploading: false,
            saveBtnLoading: false,
            showSuccess: false, // 发布成功居中动画
            successTimer: null,
            // 众筹截止时间选择器：禁止选择当前时间之前的时间
            deadlinePickerOptions: {
                disabledDate(time) {
                    return time.getTime() < Date.now() - 8.64e7 // 不能选今天之前
                },
            },
        }
    },
    computed: {
        // 每人平摊预算：预算金额 / 众筹人数（两者均为有效数字时展示）
        perPersonAmount() {
            const budget = Number(this.customForm.budget)
            const num = Number(this.customForm.crowdNumber)
            if (!this.customForm.budget || isNaN(budget) || budget < 0) return null
            if (!this.customForm.crowdNumber || isNaN(num) || num < 2) return null
            const per = budget / num
            return per % 1 === 0 ? per : per.toFixed(2)
        },
    },
    mounted() {
        // 获取上传业务ID（需求附件上传使用）
        queryUuIdInfo().then(res => { this.id = res }).catch(() => { })
    },
    methods: {
        chooseAttach(file, fileList) {
            this.attachList = fileList
            const raw = file.raw
            if (!raw) return
            const name = raw.name || ''
            const ext = name.slice(name.lastIndexOf('.')).toLowerCase()
            const allowedExts = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.pdf', '.txt', '.md', '.csv', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', '.zip', '.rar', '.7z', '.tar', '.gz']
            if (!allowedExts.includes(ext)) {
                this.$message.error('仅支持上传常见文档、图片、压缩包等格式（如 doc / xlsx / pdf / png / zip 等）')
                this.attachList = []
                return
            }
            const bizId = this.id
            const params = `?fileType=software&bizId=${bizId}&number=customDemand`
            const fd = new FormData()
            fd.append('file', raw)
            this.attachUploading = true
            this.$utils.upLoadFile(params, fd, () => { }).then(res => {
                if (!res || !res.data || !res.data.urls) {
                    const msg = (res && res.header && res.header.msg) ? res.header.msg : '上传失败，请稍后重试'
                    throw { msg }
                }
                // demandFile 为数组：上传成功后写入返回的文件路径。
                // 因 el-upload :limit="1" 只允许一个文件，这里「整体覆盖」而非 push，
                // 避免 on-change 多次触发造成的重复/空串污染；同时过滤掉空串与空白项。
                const rawUrls = res.data.urls
                const urlList = Array.isArray(rawUrls) ? rawUrls : [rawUrls]
                this.customForm.demandFile = urlList.filter(u => u && String(u).trim())
                this.$refs.customForm.validateField('demandFile')
                this.$message.success('附件上传成功')
            }).catch(err => {
                this.$message.error((err && err.msg) ? err.msg : '上传失败，请确认已登录后重试')
                this.attachList = []
                this.customForm.demandFile = []
            }).finally(() => {
                this.attachUploading = false
            })
        },
        onAttachRemove() {
            this.customForm.demandFile = []
            this.attachList = []
        },
        // 切换定制模式：从多人众筹切回个人定制时，清空「几人众筹」并移除其校验红框
        onModeChange(mode) {
            if (mode !== 'crowdfunding') {
                this.customForm.crowdNumber = ''
                this.customForm.crowdDeadline = ''
                if (this.$refs.customForm && typeof this.$refs.customForm.clearValidate === 'function') {
                    this.$refs.customForm.clearValidate('crowdNumber')
                    this.$refs.customForm.clearValidate('crowdDeadline')
                }
            }
        },
        submitCustom() {
            this.$refs.customForm.validate((valid) => {
                if (!valid) {
                    this.$message.warning('请完善标红的必填项后再提交')
                    return false
                }
                // 接口：/stage-api/software/custom/demand/release
                // Body: mode(定制模式: personal/crowdfunding, 必需)
                //       title(定制软件标题, 必需, <=30字符)
                //       demand(需求详细说明, 必需, <=500字符)
                //       demandFile(需求附件列表 array[string], 可选)
                //       userId(需求发布人用户ID <int64>, 可选, 后台查询使用)
                //       budget(预算金额 number/bigdecimal, 必需, 单位:元, >=0)
                //       budgetRemark(预算补充说明 string, 可选)
                const payload = {
                    mode: this.customForm.customMode, // personal-个人定制 / crowdfunding-多人众筹
                    title: this.customForm.title, // 定制软件标题
                    demand: this.customForm.description, // 需求详细说明
                    demandFile: (Array.isArray(this.customForm.demandFile) && this.customForm.demandFile.length)
                        ? this.customForm.demandFile.filter(f => f && String(f).trim()) // 兜底过滤空串/空白
                        : [], // 可选附件，无则传空数组
                    userId: (this.vuex_userInfo && this.vuex_userInfo.userId)
                        ? Number(this.vuex_userInfo.userId)
                        : undefined, // 可选，后台按发布人查询用；未登录时不传
                    budget: (this.customForm.budget === '' || this.customForm.budget === null || this.customForm.budget === undefined)
                        ? 0 // 兜底：已通过必填校验，正常必有值
                        : Number(this.customForm.budget), // 预算金额（元）
                    budgetRemark: this.customForm.budgetRemark || '', // 可选，无则传空串
                    // 几人众筹：仅多人众筹模式传该字段，个人定制时不传（undefined）
                    crowdNumber: this.customForm.customMode === 'crowdfunding'
                        ? (this.customForm.crowdNumber === '' || this.customForm.crowdNumber === null || this.customForm.crowdNumber === undefined
                            ? undefined
                            : Number(this.customForm.crowdNumber))
                        : undefined,
                    // 众筹截止时间：仅多人众筹模式传该字段（yyyy-MM-dd HH:mm:ss），个人定制时不传（undefined）
                    crowdDeadline: this.customForm.customMode === 'crowdfunding'
                        ? (this.customForm.crowdDeadline || undefined)
                        : undefined,
                }
                this.saveBtnLoading = true
                softwareCustomDemandRelease(payload).then(res => {
                    // 用 try/catch 隔离"成功后的本地收尾"抛错，避免污染到下面的 .catch 展示误导性的"发布失败"
                    try {
                        if (res && res.code === 200) {
                            // 居中成功动画：显示约 1.4s 后收尾（重置表单 + 通知父组件关闭弹窗/刷新）
                            this.showSuccess = true
                            if (this.successTimer) clearTimeout(this.successTimer)
                            this.successTimer = setTimeout(() => {
                                this.showSuccess = false
                                this.resetCustomForm()
                                this.$emit('success') // 通知父组件（弹窗）刷新并关闭
                            }, 2300)
                        } else {
                            this.$message.error((res && res.msg) || '发布失败')
                        }
                    } catch (innerErr) {
                        // 本地收尾（如 resetFields）出错，单独提示，不复用"发布失败"
                        console.error('[submitCustom] success handler error:', innerErr)
                        this.$message.error('发布成功，但表单重置出错：' + ((innerErr && innerErr.message) || String(innerErr)))
                    }
                }).catch(err => {
                    // 把真实错误打到控制台，便于定位
                    console.error('[submitCustom] request error:', err)
                    const status = err && err.response && err.response.status
                    const respData = err && err.response && err.response.data
                    const realMsg = (err && err.msg)
                        || (respData && (respData.msg || respData.message))
                        || (err && err.message)
                        || '发布失败，请稍后重试'
                    const tip = status ? `（HTTP ${status}）` : ''
                    this.$message.error(realMsg + tip)
                }).finally(() => {
                    this.saveBtnLoading = false
                })
            })
        },
        resetCustomForm() {
            // 记住用户当前选中的定制模式，重置表单后予以保留（不清空、不强制回退到第一项）
            const keepMode = this.customForm.customMode
            // resetFields 在某些边界场景下会抛错（比如 el-radio 的 prop/初始值不一致等），
            // 这里加 try/catch 兜底，避免重置失败把外层 Promise 推进 .catch
            try {
                if (this.$refs.customForm && typeof this.$refs.customForm.resetFields === 'function') {
                    this.$refs.customForm.resetFields()
                }
            } catch (e) {
                console.warn('[resetCustomForm] resetFields error:', e)
            }
            this.attachList = []
            this.customForm.demandFile = []
            this.customForm.customMode = keepMode // 提交成功后保留原选中的定制模式
            this.customForm.crowdNumber = '' // 重置众筹人数
            this.customForm.crowdDeadline = '' // 重置众筹截止时间
            this.customForm.title = ''
            this.customForm.description = ''
            this.customForm.budget = ''
            this.customForm.budgetRemark = ''
        },
    },
    beforeDestroy() {
        if (this.successTimer) {
            clearTimeout(this.successTimer)
            this.successTimer = null
        }
    },
}
</script>

<style lang="scss" scoped>
.custom-need-form {
    .form-box {
        max-width: 960px;
    }

    .upload-tip {
        font-size: 12px;
        color: #999;
        margin-top: 4px;
        line-height: 1.4;
    }

    // 多人众筹提示性文字
    .crowd-tip {
        font-size: 12px;
        color: #e6a23c;
        margin-top: 6px;
        line-height: 1.4;
    }
}

/* 发布成功居中动画 */
.success-mask {
    position: fixed;
    inset: 0;
    z-index: 3000;
    /* 盖在 el-dialog（~2000）之上 */
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.28);
}

.success-card {
    width: 200px;
    height: 200px;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    animation: successCardIn 0.35s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.success-icon {
    width: 72px;
    height: 72px;
}

.success-circle {
    stroke: #67c23a;
    stroke-width: 3;
    stroke-dasharray: 151;
    stroke-dashoffset: 151;
    animation: successDrawCircle 0.5s ease forwards;
}

.success-check {
    stroke: #67c23a;
    stroke-width: 4;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 48;
    stroke-dashoffset: 48;
    animation: successDrawCheck 0.35s 0.45s ease forwards;
}

.success-text {
    margin-top: 14px;
    font-size: 18px;
    font-weight: 600;
    color: #303133;
    animation: successFadeUp 0.4s 0.7s ease both;
}

.success-sub {
    margin-top: 6px;
    font-size: 13px;
    color: #909399;
    animation: successFadeUp 0.4s 0.82s ease both;
}

@keyframes successCardIn {
    0% {
        transform: scale(0.6);
        opacity: 0;
    }

    100% {
        transform: scale(1);
        opacity: 1;
    }
}

@keyframes successDrawCircle {
    to {
        stroke-dashoffset: 0;
    }
}

@keyframes successDrawCheck {
    to {
        stroke-dashoffset: 0;
    }
}

@keyframes successFadeUp {
    0% {
        transform: translateY(8px);
        opacity: 0;
    }

    100% {
        transform: translateY(0);
        opacity: 1;
    }
}

/* 弹窗整体淡入/淡出 */
.success-pop-enter-active,
.success-pop-leave-active {
    transition: opacity 0.3s ease;
}

.success-pop-enter,
.success-pop-leave-to {
    opacity: 0;
}
</style>
